import React, { useState, useEffect, useRef } from 'react';
import { Toolbar, Table, TableContainer, TableHead, TableBody, IconButton, TableRow, TableCell, TablePagination, TableSortLabel, Divider, Select, Menu, MenuItem, Paper, FormControl, Box, Button, OutlinedInput, InputAdornment } from '@mui/material';
import { Clear, ArrowDropDown } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import buildUrl from '../buildUrl';
import useSearch from '../hooks/useSearch';
import useApi from '../hooks/useApi';
import useSnackbar from '../hooks/useSnackbar';
import DownloadExportedFileDialog from './dialogs/DownloadExportedFileDialog';
import CopyContactsDialog from '../contacts/dialogs/CopyContactsDialog';
import UnsubscribeSubscribersDialog from './dialogs/UnsubscribeSubscribersDialog';
import ResubscribeSubscribersDialog from './dialogs/ResubscribeSubscribersDialog';
import BulkOperationInProgressDialog from '../contacts/dialogs/BulkOperationInProgressDialog';
import DataColumn from '../dataTable/DataColumn';
import CenteredProgress from '../loadingOverlay/CenteredProgress';
import Contact from '../contacts/Contact';
import queryClient from '../app/queryClient';

const rowsPerPageOptions = [10, 20, 50];

const columns = [
    { name: 'emailAddress', label: 'Email Address', orderDesc: false, dataType: 'text' },
    { name: 'opens', label: 'Total Opens', align: 'right', orderDesc: true, dataType: 'number' },
    { name: 'firstOpen', label: 'First Opened', align: 'right', orderDesc: false, dataType: 'dateTime' },
    { name: 'clicks', label: 'Total Clicks', align: 'right', orderDesc: true, dataType: 'number' },
    { name: 'firstClick', label: 'First Clicked', align: 'right', orderDesc: false, dataType: 'dateTime' },
    { name: 'deliveryStatus', label: 'Delivery Status', orderDesc: false, dataType: 'text' },
    { name: 'bounceCategory', label: 'Bounce Category', orderDesc: false, dataType: 'text' },
    { name: 'bounceMessage', label: 'Bounce Message', orderDesc: false, dataType: 'text' },
    { name: 'unsubscribeDate', label: 'Unsubscribed', align: 'right', orderDesc: false, dataType: 'dateTime' }
];

const dialogs = {
    EXPORT_FILE: 1,
    GROUP_CONTACTS: 2,
    UNSUBSCRIBE_CONTACTS: 3,
    RESUBSCRIBE_CONTACTS: 4,
    BULK_OPERATION: 5
};

const ActionsMenu = ({ onExport, onGroup, onUnsubscribe, onResubscribe, showResubscribe, count }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleClick = event => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleExport = () => {
        onExport();
        handleClose();
    };

    const handleGroup = () => {
        onGroup();
        handleClose();
    };

    const handleUnsubscribe = () => {
        onUnsubscribe();
        handleClose();
    };

    const handleResubscribe = () => {
        onResubscribe();
        handleClose();
    };

    return (
        <>
            <Button
                onClick={handleClick}
                endIcon={<ArrowDropDown />}
                sx={{ mr: 1 }}
                disabled={count === 0}
            >
                Actions
            </Button>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
            >
                <MenuItem onClick={handleExport}>Export Contact{count > 1 && 's'}</MenuItem>
                <MenuItem onClick={handleGroup}>Group Contact{count > 1 && 's'}</MenuItem>
                <MenuItem onClick={handleUnsubscribe}>Unsubscribe Contact{count > 1 && 's'}</MenuItem>
                {showResubscribe && <MenuItem onClick={handleResubscribe}>Resubscribe Contact{count > 1 && 's'}</MenuItem>}
            </Menu>
        </>
    );
};

const ContactsTable = ({ campaignId, onRefreshGroups, status = null, device = null, client = null, onChangeStatus = null, linkId = null, enableJumping = false, selectedGroup = 'none' }) => {
    const ref = useRef();
    const { showSnackbar } = useSnackbar();
    const { handleGet, handlePut } = useApi();
    const theme = useTheme();
    const [searchValue, setSearchValue] = useState('');
    const [tempSearchValue, setTempSearchValue] = useSearch(setSearchValue);
    const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
    const [page, setPage] = useState(0);
    const [selectedContact, setSelectedContact] = useState(null);
    const [dialog, setDialog] = useState(null);
    const [count, setCount] = useState(0);
    const [orderBy, setOrderBy] = useState('opens');
    const [orderDesc, setOrderDesc] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const [contacts, setContacts] = useState([]);
    const [mountDrawer, setMountDrawer] = useState(false);
    const [openContact, setOpenContact] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [bulkOperation, setBulkOperation] = useState(null);

    const filterColumns = () => {
        const defaultColumns = columns.filter(e => e.name !== 'bounceMessage' && e.name !== 'bounceCategory' && e.name !== 'deliveryStatus' && e.name !== 'unsubscribeDate');

        const bouncedColumns = columns.filter(e => e.name === 'emailAddress' || e.name === 'bounceMessage' || e.name === 'bounceCategory' || e.name === 'deliveryStatus');

        const unsubscribedColumns = columns.filter(e => e.name === 'emailAddress' || e.name === 'unsubscribeDate');

        return status === 'bounced' ? bouncedColumns : status === 'unsubscribed' ? unsubscribedColumns : defaultColumns;
    };

    const tableColumns = filterColumns();

    const handleFetchContactsCount = async () => {
        setIsLoading(true);

        const params = {
            search: searchValue,
            status,
            groupId: selectedGroup === 'none' ? null : selectedGroup,
            device,
            client,
            linkId
        };

        const url = buildUrl(`reports/${campaignId}/subscribers/count`, params);
        const response = await handleGet(url);

        if (!response.ok) {
            return;
        }

        const count = await response.json();

        setCount(count);
    };

    const handleFetchContacts = async () => {
        setIsLoading(true);

        const params = {
            skip: page * rowsPerPage,
            take: rowsPerPage,
            search: searchValue,
            status,
            groupId: selectedGroup === 'none' ? null : selectedGroup,
            device,
            client,
            linkId,
            orderBy: orderBy,
            isDescending: orderDesc
        };

        const url = buildUrl(`reports/${campaignId}/subscribers`, params);
        const response = await handleGet(url);

        if (!response.ok) {
            setIsLoading(false);
            return;
        }

        const contacts = await response.json();

        setContacts(contacts);
        setIsLoading(false);
    };

    const handleSort = (field, defaultOrder) => {
        if (field === orderBy) {
            setOrderDesc(!orderDesc);
        }
        else {
            setOrderBy(field);
            setOrderDesc(defaultOrder);
        }
    };

    const handleUpdateContacts = async ({ operation, destinationGroupIds = null }) => {
        setIsProcessing(true);

        const params = {
            campaignId,
            operation,
            groupId: selectedGroup === 'none' ? null : selectedGroup,
            ...(destinationGroupIds && { destinationGroupIds }),
            ...(searchValue && { search: searchValue }),
            ...(device && { device }),
            ...(client && { client }),
            ...(linkId && { linkId }),
            ...(status === 'sent' && { sent: true }),
            ...(status === 'delivered' && { delivered: true }),
            ...(status === 'failed' && { delivered: false }),
            ...(status === 'unsubscribed' && { unsubscribed: true }),
            ...(status === 'bounced' && { bounced: true }),
            ...(status === 'clicked' && { clicked: true }),
            ...(status === 'opened' && { opened: true }),
            ...(status === 'unopened' && { opened: false })
        };

        const url = buildUrl('contacts', params);

        try {
            const response = await handlePut(url);

            if (response.ok) {
                const data = await response.json();

                if (data.completed) {
                    showSnackbar('Contacts Updated', 'success');
                }
                else {
                    setBulkOperation(data);
                }
            }
        }
        catch (e) {
            console.error(e);
            showSnackbar('Failed to Update Contacts', 'error');
        }
        finally {
            setIsProcessing(false);
        }
    };

    const handleCreateGroup = async newGroup => {
        queryClient.invalidateQueries({ queryKey: ['groups'] });
        await onRefreshGroups();
        showSnackbar(`Group created: ${newGroup.name}`, 'success');
    };

    useEffect(() => {
        bulkOperation && setDialog(dialogs.BULK_OPERATION);
    }, [bulkOperation]);

    useEffect(() => {
        if (enableJumping && !isLoading) {
            ref.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [isLoading]);

    useEffect(() => {
        handleFetchContactsCount();
    }, [status, searchValue, device, client, linkId]);

    useEffect(() => {
        setPage(0);
    }, [status, searchValue, device, client, linkId, orderBy, orderDesc, rowsPerPage]);

    useEffect(() => {
        setIsLoading(true);
    }, [searchValue, device, client, linkId, orderBy, orderDesc, rowsPerPage, page]);

    useEffect(() => {
        if (status === 'bounced' || status === 'failed' || status === 'unsubscribed') {
            setOrderBy('emailAddress');
            setOrderDesc(false);
            setIsLoading(true);
        }
        else if (status === 'clicked') {
            setOrderBy('clicks');
            setOrderDesc(true);
            setIsLoading(true);
        }
        else {
            setOrderBy('opens');
            setOrderDesc(true);
            setIsLoading(true);
        }
    }, [status]);

    useEffect(() => {
        isLoading && handleFetchContacts();
    }, [isLoading]);

    useEffect(() => {
        if (selectedContact) {
            setMountDrawer(true);
            return;
        }

        setOpenContact(false);

        const timeout = setTimeout(() => {
            setMountDrawer(false);
        }, theme.transitions.duration.leavingScreen);

        return () => clearTimeout(timeout);
    }, [selectedContact]);

    return (
        <>
            <div ref={ref}>
                <Paper variant="outlined">
                    <Toolbar disableGutters sx={{ mx: 1 }}>
                        <Box sx={{ flexGrow: 1 }}>
                            {(status && onChangeStatus) && (
                                <FormControl margin="dense" size="small" sx={{ ml: 1 }}>
                                    <Select
                                        value={status}
                                        onChange={e => onChangeStatus(e.target.value)}
                                    >
                                        <MenuItem value={'selected'}>All Contacts</MenuItem>
                                        <MenuItem value={'sent'}>Sent</MenuItem>
                                        <MenuItem value={'delivered'}>Delivered</MenuItem>
                                        <MenuItem value={'opened'}>Unique Opens</MenuItem>
                                        <MenuItem value={'clicked'}>Unique Clicks</MenuItem>
                                        <MenuItem value={'unopened'}>Unopened</MenuItem>
                                        <MenuItem value={'bounced'}>Bounced</MenuItem>
                                        <MenuItem value={'failed'}>Failed</MenuItem>
                                        <MenuItem value={'unsubscribed'}>Unsubscribed</MenuItem>
                                    </Select>
                                </FormControl>
                            )}
                            <OutlinedInput
                                value={tempSearchValue}
                                onChange={e => setTempSearchValue(e.target.value)}
                                placeholder="Search Contacts..."
                                size="small"
                                sx={{ m: 1 }}
                                endAdornment={(
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setTempSearchValue('')}
                                            edge="end"
                                            disabled={!Boolean(tempSearchValue)}
                                        >
                                            <Clear />
                                        </IconButton>
                                    </InputAdornment>
                                )}
                            />
                        </Box>
                        <ActionsMenu
                            onUnsubscribe={() => setDialog(dialogs.UNSUBSCRIBE_CONTACTS)}
                            onResubscribe={() => setDialog(dialogs.RESUBSCRIBE_CONTACTS)}
                            onGroup={() => setDialog(dialogs.GROUP_CONTACTS)}
                            onExport={() => setDialog(dialogs.EXPORT_FILE)}
                            showResubscribe={status === 'bounced'}
                            count={count}
                        />
                    </Toolbar>
                    <Divider />
                    <TableContainer>
                        {(isLoading || isProcessing) ? (
                            <Box sx={{ py: 4 }}>
                                <CenteredProgress colour="primary" />
                            </Box>
                        ) : (
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        {tableColumns.map(col => (
                                            <TableCell key={col.name} component="th" scope="row" align={col.align}>
                                                <TableSortLabel
                                                    active={orderBy === col.name}
                                                    direction={orderDesc ? 'desc' : 'asc'}
                                                    onClick={() => handleSort(col.name, col.orderDesc)}
                                                    sx={{ whiteSpace: 'pre' }}
                                                >
                                                    {col.label}
                                                </TableSortLabel>
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {contacts.map(e => (
                                        <TableRow
                                            key={e.id}
                                            hover={true}
                                            onClick={() => setSelectedContact(e)}
                                        >
                                            {tableColumns.map(col => (
                                                <DataColumn
                                                    key={col.name}
                                                    align={col.align}
                                                    dataType={col.dataType}
                                                    value={e[col.name]}
                                                    cursor="pointer"
                                                />
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={rowsPerPageOptions}
                        component="div"
                        count={count}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(e, newPage) => setPage(newPage)}
                        onRowsPerPageChange={e => setRowsPerPage(e.target.value)}
                    />
                </Paper>
            </div>
            {mountDrawer && (
                <Contact
                    onClose={() => setSelectedContact(null)}
                    id={selectedContact?.id}
                    open={openContact}
                    onRefresh={handleFetchContactsCount}
                    onMount={() => setOpenContact(true)}
                    campaignId={campaignId}
                />
            )}
            <DownloadExportedFileDialog
                open={dialog === dialogs.EXPORT_FILE}
                onClose={() => setDialog(null)}
                campaignId={campaignId}
                search={searchValue}
                status={status}
                groupId={selectedGroup === 'none' ? null : selectedGroup}
                device={device}
                client={client}
                linkId={linkId}
                orderBy={orderBy}
                isDescending={orderDesc}
                count={count}
            />
            <CopyContactsDialog
                open={dialog === dialogs.GROUP_CONTACTS}
                onClose={() => setDialog(null)}
                onConfirm={destinationGroupIds => handleUpdateContacts({ operation: 'copy', destinationGroupIds })}
                onCreateGroup={handleCreateGroup}
            />
            <UnsubscribeSubscribersDialog
                open={dialog === dialogs.UNSUBSCRIBE_CONTACTS}
                onClose={() => setDialog(null)}
                onSubmit={() => handleUpdateContacts({ operation: 'unsubscribe' })}
                count={count}
            />
            <ResubscribeSubscribersDialog
                open={dialog === dialogs.RESUBSCRIBE_CONTACTS}
                onClose={() => setDialog(null)}
                onSubmit={() => handleUpdateContacts({ operation: 'resubscribe' })}
                count={count}
            />
            <BulkOperationInProgressDialog
                open={dialog === dialogs.BULK_OPERATION}
                onClose={() => {
                    setDialog(null);
                    setBulkOperation(null);
                }}
                jobId={bulkOperation?.jobId}
            />
        </>
    );
};

export default ContactsTable;